import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { Add, Devices, Edit, History, Refresh, RestartAlt, Sync, SwapHoriz, VpnKey } from '@mui/icons-material'
import { deviceService } from '../services/api'
import { DeviceRecord, DeviceUpdateLogRecord, RenewDeviceSubscriptionPayload } from '../types'
import EditDeviceDialog from '../components/EditDeviceDialog'

const unwrapData = <T,>(response: any): T => (response?.data ?? response) as T

const formatDate = (value?: string | null) => {
  if (!value) return '—'
  return new Date(value).toLocaleString('ar-IQ', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const apiErrorMessage = (error: any, fallback: string) => {
  const data = error?.response?.data
  const msg = data?.message ?? data?.Message
  if (msg) return String(msg)

  const status = error?.response?.status
  if (status === 404) return 'العملية غير متوفرة على السيرفر — يلزم تحديث POSActivation.API'
  if (status === 403) return 'غير مصرح — فك الربط للمالك (Owner) فقط'
  if (status === 401) return 'انتهت الجلسة — سجّل الدخول مجدداً'
  if (status >= 500) return 'خطأ في السيرفر — راجع سجلات POSActivation.API'

  return error?.message ? String(error.message) : fallback
}

const getStatusLabel = (status: DeviceRecord['status']) => {
  switch (status) {
    case 'Active':
      return 'مفعل'
    case 'Inactive':
      return 'بانتظار التفعيل'
    case 'Suspended':
      return 'موقوف'
    default:
      return status
  }
}

export default function DevicesPage() {
  const navigate = useNavigate()
  const [devices, setDevices] = useState<DeviceRecord[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [renewDialogOpen, setRenewDialogOpen] = useState(false)
  const [changeDialogOpen, setChangeDialogOpen] = useState(false)
  const [renewingDevice, setRenewingDevice] = useState<DeviceRecord | null>(null)
  const [changingDevice, setChangingDevice] = useState<DeviceRecord | null>(null)
  const [renewSaving, setRenewSaving] = useState(false)
  const [changeSaving, setChangeSaving] = useState(false)
  const [resetSavingId, setResetSavingId] = useState<string | null>(null)
  const [historyDevice, setHistoryDevice] = useState<DeviceRecord | null>(null)
  const [editingDevice, setEditingDevice] = useState<DeviceRecord | null>(null)
  const [renewForm, setRenewForm] = useState<RenewDeviceSubscriptionPayload>({
    subscriptionType: 'Monthly',
    subscriptionDurationMonths: undefined,
    renewalWarningDays: 3,
    gracePeriodDays: 3,
  })

  const fetchDevices = async () => {
    try {
      setLoading(true)
      setError('')
      const response = await deviceService.getAll()
      setDevices(unwrapData<DeviceRecord[]>(response) ?? [])
    } catch (requestError: any) {
      setError(requestError?.response?.data?.message || 'فشل في تحميل الأجهزة')
      setDevices([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchDevices()
  }, [])

  const filteredDevices = useMemo(() => {
    const query = searchTerm.trim().toLowerCase()
    if (!query) return devices

    return devices.filter((device) =>
      [
        device.customerName,
        device.deviceCode,
        device.activationCode,
        device.mainDeviceCode || '',
        device.mainActivationCode || '',
        device.mainComputerName || '',
        device.computerName || '',
        device.notes || '',
        device.type,
      ]
        .join(' ')
        .toLowerCase()
        .includes(query)
    )
  }, [devices, searchTerm])

  const getSubscriptionLabel = (device: DeviceRecord) => {
    if (device.subscriptionType === 'Trial') {
      return `تجريبي${device.subscriptionDurationMonths ? ` ${device.subscriptionDurationMonths} شهر` : ''}`
    }

    if (device.subscriptionType === 'Monthly') return 'شهري'
    if (device.subscriptionType === 'Yearly') return 'سنوي'
    return 'دائمي'
  }

  const getSubscriptionStateLabel = (state: DeviceRecord['subscriptionState']) => {
    switch (state) {
      case 'PendingActivation':
        return 'بانتظار التفعيل'
      case 'Active':
        return 'فعال'
      case 'ExpiringSoon':
        return 'قارب على الانتهاء'
      case 'GracePeriod':
        return 'فترة سماح'
      case 'Expired':
        return 'منتهي'
      case 'Permanent':
        return 'دائمي'
      default:
        return state
    }
  }

  const getSubscriptionStateColor = (state: DeviceRecord['subscriptionState']) => {
    switch (state) {
      case 'Active':
      case 'Permanent':
        return 'success'
      case 'ExpiringSoon':
      case 'PendingActivation':
        return 'warning'
      case 'GracePeriod':
        return 'info'
      case 'Expired':
        return 'error'
      default:
        return 'default'
    }
  }

  const openChangeDialog = (device: DeviceRecord) => {
    setChangingDevice(device)
    setRenewForm({
      subscriptionType: device.subscriptionType === 'Permanent' ? 'Permanent' : device.subscriptionType,
      subscriptionDurationMonths: device.subscriptionDurationMonths ?? undefined,
      renewalWarningDays: 3,
      gracePeriodDays: 3,
    })
    setChangeDialogOpen(true)
  }

  const submitChange = async () => {
    if (!changingDevice) return

    try {
      setChangeSaving(true)
      const payload: RenewDeviceSubscriptionPayload = {
        ...renewForm,
        subscriptionDurationMonths: renewForm.subscriptionType === 'Trial' ? renewForm.subscriptionDurationMonths || 1 : undefined,
      }
      await deviceService.changeSubscription(changingDevice.id, payload)
      setChangeDialogOpen(false)
      setChangingDevice(null)
      await fetchDevices()
    } catch (requestError: any) {
      setError(requestError?.response?.data?.message || 'تعذر تحويل الاشتراك')
    } finally {
      setChangeSaving(false)
    }
  }

  const resetActivation = async (device: DeviceRecord) => {
    const confirmed = window.confirm(
      `فك ربط الجهاز «${device.deviceCode}»؟\n\n` +
        `سيُبقى رمز التفعيل: ${device.activationCode}\n` +
        'ويمكن للزبون إعادة التفعيل على نفس الحاسبة بعد الفورمات أو على حاسبة جديدة.'
    )
    if (!confirmed) return

    try {
      setResetSavingId(device.id)
      setError('')
      await deviceService.resetActivation(device.id)
      await fetchDevices()
    } catch (requestError: any) {
      setError(apiErrorMessage(requestError, 'تعذر فك ربط الجهاز'))
    } finally {
      setResetSavingId(null)
    }
  }

  const isRealDeviceBinding = (deviceCode: string) =>
    !/^(MAIN|SUB|MOB|APP|API)-/i.test((deviceCode || '').trim())

  const openRenewDialog = (device: DeviceRecord) => {
    setRenewingDevice(device)
    setRenewForm({
      subscriptionType: device.subscriptionType === 'Permanent' ? 'Yearly' : device.subscriptionType,
      subscriptionDurationMonths: device.subscriptionDurationMonths ?? undefined,
      renewalWarningDays: 3,
      gracePeriodDays: 3,
    })
    setRenewDialogOpen(true)
  }

  const submitRenewal = async () => {
    if (!renewingDevice) return

    try {
      setRenewSaving(true)
      const payload: RenewDeviceSubscriptionPayload = {
        ...renewForm,
        subscriptionDurationMonths: renewForm.subscriptionType === 'Trial' ? renewForm.subscriptionDurationMonths || 1 : undefined,
      }
      await deviceService.renewSubscription(renewingDevice.id, payload)
      setRenewDialogOpen(false)
      setRenewingDevice(null)
      await fetchDevices()
    } catch (requestError: any) {
      setError(requestError?.response?.data?.message || 'تعذر تجديد الاشتراك')
    } finally {
      setRenewSaving(false)
    }
  }

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2} sx={{ mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
            الأجهزة
          </Typography>
          <Typography color="text.secondary">
            إضافة الأجهزة تتم من صفحة العميل حتى تبقى الأجهزة مرتبطة بالعميل بشكل صحيح.
          </Typography>
        </Box>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
          <Button variant="outlined" startIcon={<Refresh />} onClick={fetchDevices} disabled={loading}>
            تحديث
          </Button>
          <Button variant="contained" startIcon={<Add />} onClick={() => navigate('/customers')}>
            إضافة جهاز من صفحة العملاء
          </Button>
        </Stack>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{error}</Alert>}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Card sx={{ borderRadius: 3 }}><CardContent><Typography color="text.secondary">إجمالي الأجهزة</Typography><Typography variant="h4" sx={{ fontWeight: 800 }}>{devices.length}</Typography></CardContent></Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card sx={{ borderRadius: 3 }}><CardContent><Typography color="text.secondary">أجهزة رئيسية</Typography><Typography variant="h4" sx={{ fontWeight: 800 }}>{devices.filter((device) => device.type === 'Main').length}</Typography></CardContent></Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card sx={{ borderRadius: 3 }}><CardContent><Typography color="text.secondary">أجهزة فرعية</Typography><Typography variant="h4" sx={{ fontWeight: 800 }}>{devices.filter((device) => device.type === 'Sub').length}</Typography></CardContent></Card>
          <Card sx={{ borderRadius: 3 }}><CardContent><Typography color="text.secondary">موبايل Mera Route</Typography><Typography variant="h4" sx={{ fontWeight: 800 }}>{devices.filter((device) => device.type === 'Mobile').length}</Typography></CardContent></Card>
        </Grid>
      </Grid>

      <Paper sx={{ p: 2.5, mb: 3, borderRadius: 3 }}>
        <TextField fullWidth label="بحث بالعميل أو الملاحظات أو رقم الجهاز أو مفتاح التفعيل" value={searchTerm} onChange={(event) => setSearchTerm(event.target.value)} />
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
      ) : filteredDevices.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: 'center', borderRadius: 4 }}>
          <Devices sx={{ fontSize: 56, color: 'primary.main', mb: 2 }} />
          <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>لا توجد أجهزة حالياً</Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>انتقل إلى صفحة العملاء، افتح تفاصيل العميل، ثم أضف جهازاً رئيسياً أو فرعياً.</Typography>
          <Button variant="contained" startIcon={<Add />} onClick={() => navigate('/customers')}>الذهاب إلى العملاء</Button>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {filteredDevices.map((device) => (
            <Grid item xs={12} md={6} lg={4} key={device.id}>
              <Card sx={{ height: '100%', borderRadius: 4, boxShadow: '0 14px 30px rgba(15,23,42,0.08)' }}>
                <CardContent sx={{ p: 3 }}>
                  <Stack direction="row" justifyContent="space-between" spacing={2} sx={{ mb: 2 }}>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 800 }}>{device.customerName}</Typography>
                      <Typography color="text.secondary" variant="body2">
                        {device.type === 'Main'
                          ? 'جهاز رئيسي'
                          : device.type === 'Mobile'
                            ? 'موبايل Mera Route'
                            : device.type === 'MeraApp'
                              ? 'Mera موبايل'
                              : device.type === 'Api'
                                ? 'API'
                                : 'جهاز فرعي'}
                      </Typography>
                    </Box>
                    <Chip label={getStatusLabel(device.status)} color={device.status === 'Active' ? 'success' : device.status === 'Suspended' ? 'error' : 'warning'} />
                  </Stack>

                  <Stack spacing={1}>
                    {device.type === 'Api' ? (
                      <>
                        <Typography variant="body2" sx={{ fontWeight: 800 }}>
                          رقم الرئيسية: {device.mainComputerName || '—'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          تفعيل الرئيسية: {device.mainActivationCode || '—'}
                        </Typography>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <VpnKey fontSize="small" color="action" />
                          <Typography variant="body2">تفعيل API: {device.activationCode}</Typography>
                        </Stack>
                        <Chip
                          size="small"
                          label={device.apiHostingKind === 'Host' ? 'هوست' : 'ويندوز'}
                          color={device.apiHostingKind === 'Host' ? 'warning' : 'success'}
                          variant="outlined"
                        />
                      </>
                    ) : (
                      <>
                        {device.computerName ? (
                          <Typography variant="body2" sx={{ fontWeight: 800 }}>
                            رقم الجهاز: {device.computerName}
                          </Typography>
                        ) : null}
                        <Typography variant="body2" color="text.secondary">Device Code: {device.deviceCode}</Typography>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <VpnKey fontSize="small" color="action" />
                          <Typography variant="body2">{device.activationCode}</Typography>
                        </Stack>
                      </>
                    )}
                    {device.routeSiteCode ? (
                      <Typography variant="body2" color="primary.main" sx={{ fontWeight: 700 }}>
                        Mera Route: {device.routeSiteCode}
                      </Typography>
                    ) : null}
                    <Typography variant="body2" color="text.secondary">الإصدار: {device.version}</Typography>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 1.25,
                        borderRadius: 2,
                        bgcolor: device.lastUpdatedAt ? 'rgba(46,125,50,0.06)' : 'rgba(0,0,0,0.02)',
                        borderColor: device.lastUpdatedAt ? 'success.light' : 'divider',
                      }}
                    >
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ fontWeight: 700 }}>
                        آخر تحديث (تاريخ ووقت)
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 800, color: device.lastUpdatedAt ? 'success.main' : 'text.secondary' }}>
                        {device.lastUpdatedAt ? formatDate(device.lastUpdatedAt) : 'لا يوجد تحديث مسجّل بعد'}
                      </Typography>
                      {device.lastUpdatedByName ? (
                        <Typography variant="caption" color="text.secondary" display="block">
                          بواسطة: {device.lastUpdatedByName}
                          {device.lastUpdatedVersion ? ` · v${device.lastUpdatedVersion}` : ''}
                          {device.lastUpdateProduct ? ` · ${device.lastUpdateProduct}` : ''}
                        </Typography>
                      ) : null}
                      {(device.recentUpdateLogs?.length ?? 0) > 0 && (
                        <Button
                          size="small"
                          startIcon={<History />}
                          onClick={() => setHistoryDevice(device)}
                          sx={{ mt: 0.75, px: 0 }}
                        >
                          سجل التحديثات ({device.recentUpdateLogs!.length})
                        </Button>
                      )}
                    </Paper>
                    <Typography variant="body2" color="text.secondary">الجهاز الرئيسي: {device.mainDeviceCode || 'هذا جهاز رئيسي'}</Typography>
                    <Typography variant="body2" color="text.secondary">نوع الاشتراك: {getSubscriptionLabel(device)}</Typography>
                    <Typography variant="body2" color="text.secondary">تاريخ الانتهاء: {device.subscriptionEndsAt ? new Date(device.subscriptionEndsAt).toLocaleDateString('ar-IQ') : 'غير محدد'}</Typography>
                    <Typography variant="body2" color="text.secondary">{device.subscriptionAlertMessage}</Typography>
                    {device.meraApiUrl ? (
                      <Typography variant="body2" color="primary.main" sx={{ fontWeight: 700 }}>
                        Mera API: {device.meraApiUrl}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="warning.main">لا يوجد عنوان Mera API مسجّل</Typography>
                    )}
                    <Alert severity="info" sx={{ borderRadius: 2, py: 0.5 }}>
                      <Typography variant="caption" sx={{ fontWeight: 800, display: 'block' }}>تفاصيل التفعيل</Typography>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {device.notes?.trim() || 'لا توجد تفاصيل مسجّلة لهذا الجهاز.'}
                      </Typography>
                    </Alert>
                    <Stack direction="row" spacing={1} sx={{ pt: 1 }} flexWrap="wrap">
                      <Chip
                        label={getSubscriptionStateLabel(device.subscriptionState)}
                        color={getSubscriptionStateColor(device.subscriptionState) as any}
                        size="small"
                      />
                      <Button size="small" variant="contained" startIcon={<Edit />} onClick={() => setEditingDevice(device)}>
                        تعديل
                      </Button>
                      <Button size="small" startIcon={<SwapHoriz />} onClick={() => openChangeDialog(device)}>
                        تحويل الاشتراك
                      </Button>
                      {(device.status === 'Active' || isRealDeviceBinding(device.deviceCode)) && (
                        <Button
                          size="small"
                          color="warning"
                          startIcon={<RestartAlt />}
                          disabled={resetSavingId === device.id}
                          onClick={() => resetActivation(device)}
                        >
                          {resetSavingId === device.id ? 'جاري الفك...' : 'فك ربط الجهاز'}
                        </Button>
                      )}
                      {(device.subscriptionState === 'ExpiringSoon' || device.subscriptionState === 'GracePeriod' || device.subscriptionState === 'Expired') && (
                        <Button size="small" startIcon={<Sync />} onClick={() => openRenewDialog(device)}>
                          تجديد الاشتراك
                        </Button>
                      )}
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <EditDeviceDialog
        open={!!editingDevice}
        device={editingDevice}
        onClose={() => setEditingDevice(null)}
        onSaved={async () => {
          await fetchDevices()
        }}
        onError={(message) => setError(message)}
      />

      <Dialog open={!!historyDevice} onClose={() => setHistoryDevice(null)} fullWidth maxWidth="sm">
        <DialogTitle>سجل تحديثات الجهاز</DialogTitle>
        <DialogContent>
          {historyDevice && (
            <Stack spacing={1.5} sx={{ pt: 1 }}>
              <Alert severity="info" sx={{ borderRadius: 2 }}>
                {historyDevice.customerName} — {historyDevice.activationCode}
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  آخر تحديث: {historyDevice.lastUpdatedAt ? formatDate(historyDevice.lastUpdatedAt) : '—'}
                  {historyDevice.lastUpdatedByName ? ` — ${historyDevice.lastUpdatedByName}` : ''}
                </Typography>
              </Alert>
              {(historyDevice.recentUpdateLogs?.length ?? 0) === 0 ? (
                <Alert severity="warning">لا يوجد سجل تحديثات لهذا الجهاز بعد.</Alert>
              ) : (
                historyDevice.recentUpdateLogs!.map((log: DeviceUpdateLogRecord) => (
                  <Paper key={log.id} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                    <Typography sx={{ fontWeight: 800 }}>{formatDate(log.updatedAt)}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      بواسطة: {log.updatedByName || '—'}
                      {log.product ? ` · ${log.product}` : ''}
                    </Typography>
                    <Typography variant="body2">
                      الإصدار: {log.fromVersion || '?'} → {log.toVersion || '?'}
                    </Typography>
                    {log.notes ? (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {log.notes}
                      </Typography>
                    ) : null}
                  </Paper>
                ))
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setHistoryDevice(null)}>إغلاق</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={renewDialogOpen} onClose={() => setRenewDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>تجديد اشتراك الجهاز</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {renewingDevice && (
              <Alert severity="info">
                الجهاز: {renewingDevice.deviceCode} | العميل: {renewingDevice.customerName}
              </Alert>
            )}
            <TextField
              select
              label="نوع الاشتراك الجديد"
              value={renewForm.subscriptionType}
              onChange={(event) =>
                setRenewForm((prev) => ({
                  ...prev,
                  subscriptionType: event.target.value as RenewDeviceSubscriptionPayload['subscriptionType'],
                }))
              }
              fullWidth
            >
              <MenuItem value="Trial">تجريبي</MenuItem>
              <MenuItem value="Monthly">شهري</MenuItem>
              <MenuItem value="Yearly">سنوي</MenuItem>
              <MenuItem value="Permanent">دائمي مفتوح</MenuItem>
            </TextField>
            {renewForm.subscriptionType === 'Trial' && (
              <TextField
                select
                label="مدة التجريبي"
                value={renewForm.subscriptionDurationMonths || 1}
                onChange={(event) =>
                  setRenewForm((prev) => ({
                    ...prev,
                    subscriptionDurationMonths: Number(event.target.value),
                  }))
                }
                fullWidth
              >
                <MenuItem value={1}>شهر واحد</MenuItem>
                <MenuItem value={2}>شهرين</MenuItem>
                <MenuItem value={3}>ثلاثة أشهر</MenuItem>
              </TextField>
            )}
            <Alert severity="warning">
              بعد التجديد يرجع الجهاز إلى الحالة الفعالة، والتنبيه يظل قبل 3 أيام مع فترة سماح 3 أيام.
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setRenewDialogOpen(false)}>إلغاء</Button>
          <Button variant="contained" onClick={submitRenewal} disabled={renewSaving}>
            {renewSaving ? 'جاري التجديد...' : 'تجديد الآن'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={changeDialogOpen} onClose={() => setChangeDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>تحويل نوع الاشتراك</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {changingDevice && (
              <Alert severity="info">
                الجهاز: {changingDevice.deviceCode} | العميل: {changingDevice.customerName}
              </Alert>
            )}
            <TextField
              select
              label="نوع الاشتراك الجديد"
              value={renewForm.subscriptionType}
              onChange={(event) =>
                setRenewForm((prev) => ({
                  ...prev,
                  subscriptionType: event.target.value as RenewDeviceSubscriptionPayload['subscriptionType'],
                }))
              }
              fullWidth
            >
              <MenuItem value="Trial">تجريبي</MenuItem>
              <MenuItem value="Monthly">شهري</MenuItem>
              <MenuItem value="Yearly">سنوي</MenuItem>
              <MenuItem value="Permanent">دائمي مفتوح</MenuItem>
            </TextField>
            {renewForm.subscriptionType === 'Trial' && (
              <TextField
                select
                label="مدة التجريبي"
                value={renewForm.subscriptionDurationMonths || 1}
                onChange={(event) =>
                  setRenewForm((prev) => ({
                    ...prev,
                    subscriptionDurationMonths: Number(event.target.value),
                  }))
                }
                fullWidth
              >
                <MenuItem value={1}>شهر واحد</MenuItem>
                <MenuItem value={2}>شهرين</MenuItem>
                <MenuItem value={3}>ثلاثة أشهر</MenuItem>
              </TextField>
            )}
            <Alert severity="warning">
              يُعاد حساب مدة الاشتراك من اليوم (مثلاً تجريبي → دائمي أو شهري).
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setChangeDialogOpen(false)}>إلغاء</Button>
          <Button variant="contained" onClick={submitChange} disabled={changeSaving}>
            {changeSaving ? 'جاري التحويل...' : 'تأكيد التحويل'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
